import React, { Dispatch, SetStateAction, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Option } from 'slim-select/dist/data.d';

import Form from '../../../components/form/form/Form';
import FormItem from '../../../components/form/form-item/FormItem';
import Button from '../../../components/form/button/Button';
import Input from '../../../components/form/input/Input';
import TextArea from '../../../components/form/text-area/TextArea';
import ProjectInfoBox from './project-info-box/ProjectInfoBox';
import ProjectInfoPictureUpload from './ProjectInfoPictureUpload';
import LogisticPlanUpload from './LogisticPlanUpload';
import Select from '../../../components/form/select/Select';
import { getProjectDetails, saveProjectDetails } from '../../../core/state/project/asyncActions';
import { RootState } from '../../../core/state/state';
import UnsavedChangesModal from '../../components/unsaved-changes-modal/UnsavedChangesModal';
import { SettingsExpanded } from '../../../core/types/settings/SettingsExpanded';
import { IMG_LOCATION } from '../../../core/constants/api';
import { ProjectDetailsRequest } from '../../../core/types/services/ProjectDetailsRequest';
import { isReadAllRole } from '../../../core/state/user/selectors';
import { ContactPerson } from '../../../core/state/project/types';
import { Actions } from '../../../core/state/project/actions';

interface Props {
  isExpanded: SettingsExpanded;
  setExpanded: Dispatch<SetStateAction<SettingsExpanded>>;
  contacts: ContactPerson[];
}

const ProjectInfo: React.FC<Props> = ({ isExpanded, setExpanded, contacts }) => {
  const { t } = useTranslation();
  const { projectId } = useParams<{ projectId: string }>();
  const projectDetails = useSelector((state: RootState) => state.projects?.projectDetails);
  const dispatch = useDispatch();
  const isUserReader = useSelector(isReadAllRole);

  const {
    control,
    formState: { errors },
    handleSubmit,
    setValue,
    formState
  } = useForm<ProjectDetailsRequest>({
    defaultValues: {
      constructionSiteId: 0,
      constructionSiteAddress: '',
      firstContactPersonsId: null,
      secondContactPersonsId: null,
      firstUserAccountID: null,
      secondUserAccountID: null,
      deliveryNotes: ''
    }
  });

  React.useEffect(() => {
    dispatch(getProjectDetails(Number(projectId)));
  }, [projectId, dispatch]);

  React.useEffect(() => {
    dispatch(Actions.setProjectDetails(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExpanded]);

  const onSubmit = handleSubmit((data) => {
    if (projectDetails) {
      dispatch(saveProjectDetails(data, Number(projectId)));
      setExpanded({ ...isExpanded, projectInfo: false });
    }
  });

  const onCancel = () => {
    setExpanded({ ...isExpanded, projectInfo: false });
  };

  const contactsToOption = (contact: ContactPerson): Option => ({
    value: contact.userAccountID.toString(),
    text: `${contact.firstName} ${contact.lastName} ${
      contact.mobilePhoneNumber || contact.title ? `(${contact.mobilePhoneNumber ?? ''} ${contact.title ?? ''})` : ''
    }`
  });

  const userOptions = useMemo(() => contacts?.map(contactsToOption), [contacts]);

  React.useEffect(() => {
    if (projectDetails && !formState.isDirty) {
      setValue('constructionSiteAddress', projectDetails.constructionSite.address);
      setValue('deliveryNotes', projectDetails.constructionSite.deliveryNotes);
      setValue('constructionSiteId', projectDetails.constructionSite.id);
      if (projectDetails.projectContacts) {
        if (projectDetails.projectContacts[0]) {
          setValue('firstContactPersonsId', Number(projectDetails.projectContacts[0].id));
          setValue('firstUserAccountID', Number(projectDetails.projectContacts[0].userAccountID));
        }
        if (projectDetails.projectContacts[1]) {
          setValue('secondContactPersonsId', Number(projectDetails.projectContacts[1].id));
          setValue('secondUserAccountID', Number(projectDetails.projectContacts[1].userAccountID));
        }
      }
    }
  }, [projectDetails, setValue, formState]);

  return (
    <div className="project-info">
      <Form onSubmit={onSubmit}>
        <FormItem text={t('projectSettings.projectInfo.generalInfo')}>
          <ProjectInfoBox
            img={projectDetails?.image.name ? `${IMG_LOCATION}/${projectDetails?.image.fullName}` : undefined}
            number={projectDetails?.code}
            name={projectDetails?.name}
            area={projectDetails?.constructionArea}
          />
        </FormItem>
        <FormItem text={t('projectSettings.projectInfo.address')}>
          <Controller
            name="constructionSiteAddress"
            control={control}
            render={({ field: { onChange, value } }) => (
              <Input
                type="text"
                onChange={(e) => {
                  onChange(e.target.value);
                }}
                id="constructionSiteAddress"
                error={errors.constructionSiteAddress?.message}
                name="constructionSiteAddress"
                value={value ?? ''}
              />
            )}
          />
        </FormItem>
        <FormItem text={t('projectSettings.projectInfo.projectImage')}>
          <ProjectInfoPictureUpload />
        </FormItem>
        <FormItem text={t('projectSettings.projectInfo.showContactDetails')}>
          {projectDetails && (
            <Controller
              name="firstUserAccountID"
              control={control}
              render={({ field: { onChange, value } }) => (
                <Select
                  onChange={(option) => {
                    const { value: v } = option as Option;
                    if (v) {
                      onChange(Number(v));
                    } else {
                      onChange(null);
                    }
                  }}
                  options={userOptions}
                  addPlaceholderOption
                  value={String(value)}
                />
              )}
            />
          )}
          <div className="mb-4" />
          {projectDetails && (
            <Controller
              name="secondUserAccountID"
              control={control}
              render={({ field: { onChange, value } }) => (
                <Select
                  onChange={(option) => {
                    const { value: v } = option as Option;
                    if (v) {
                      onChange(Number(v));
                    } else {
                      onChange(null);
                    }
                  }}
                  options={userOptions}
                  addPlaceholderOption
                  value={String(value)}
                />
              )}
            />
          )}
        </FormItem>
        <FormItem text={t('projectSettings.projectInfo.logisticPlanFile')}>
          <LogisticPlanUpload />
        </FormItem>
        <FormItem text={t('projectSettings.projectInfo.deliveryNotes')}>
          <Controller
            name="deliveryNotes"
            control={control}
            render={({ field: { onChange, value } }) => (
              <TextArea
                id="deliveryNotes"
                name="deliveryNotes"
                onChange={(e) => {
                  onChange(e.target.value);
                }}
                placeholder={t('projectSettings.projectInfo.makeNote')}
                error={errors.deliveryNotes?.message}
                value={value ?? ''}
              />
            )}
          />
        </FormItem>
        <div className="row">
          {!isUserReader && (
            <div className="offset-3 col-9">
              <Button buttonTypes={['blue-outline', 'transparent']} className="mr-2" onClick={onCancel}>
                {t('common.cancel')}
              </Button>
              <Button buttonTypes={['blue']} type="submit">
                {t('common.saveAndClose')}
              </Button>
            </div>
          )}
        </div>
        <UnsavedChangesModal showIf={formState.isDirty} onSaveExit={() => onSubmit()} />
      </Form>
    </div>
  );
};

export default ProjectInfo;
